import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { supabase } from "@/lib/supabase/supabaseClient";
import { Logger } from "@/lib/logger/Logger";
import {
	JobSearchResult,
	PatientSearchResult,
} from "@/dentlab/src/store/Search/types";
import { JobDocumentTypeEnum } from "@/dentlab/src/pages/job-page/job-document/job-document.types";
import { showNotification } from "../Central/selectors";

export interface SearchSlice {
	initialize: () => Promise<void>;
	fetchSearchResults: (
		query: string
	) => Promise<{ jobs: JobSearchResult[]; patients: PatientSearchResult[] }>;
	fetchSearchResultsPatients: (
		query: string
	) => Promise<PatientSearchResult[]>;
	fetchSearchResultsJobs: (query: string) => Promise<JobSearchResult[]>;
}

export const createSearchStore: StateCreator<SearchSlice> = (set, get) => ({
	initialize: async () => {
		const { getSession, getProfile, getOrganization } =
			useCentralStore.getState();

		const userId = await getSession();
		if (!userId) return;
		await getProfile(userId);
		await getOrganization();
	},
	fetchSearchResults: async (query: string) => {
		const [jobs, patients] = await Promise.all([
			get().fetchSearchResultsJobs(query),
			get().fetchSearchResultsPatients(query),
		]);
		return { jobs, patients };
	},
	fetchSearchResultsPatients: async (query: string) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization id found");
			return [];
		}
		const { data, error } = await supabase.rpc("search_patients", {
			query,
			p_organization_id: organizationId,
		});

		if (error) {
			Logger.log(error);
			showNotification({
				message: "Fehler beim Suchen nach Patienten",
				type: "error",
			});
		}

		return data || [];
	},
	fetchSearchResultsJobs: async (query: string) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization id found");
			return [];
		}
		const { data, error } = await supabase.rpc("search_jobs", {
			query,
			p_organization_id: organizationId,
		});

		if (error) {
			Logger.log(error);
			showNotification({
				message: "Fehler beim Suchen nach Aufträgen",
				type: "error",
			});
		}

		return (data || []).map(job => ({
			...job,
			job_document_types: job.job_document_types as JobDocumentTypeEnum[]
		}));
	},
});
